"use client";

import { useRef, useState, type FormEvent } from "react";
import { Button } from "../common/button";
import { Textarea } from "../common/Textarea";
import type { SupportAttachment, SupportMessage, SupportTicketSummary } from "./types";

interface SupportTicketThreadProps {
  ticket: SupportTicketSummary;
  /** True if the current viewer is an admin (shows INTERNAL messages + lets them post internal notes). */
  isAdmin?: boolean;
  /** Where to POST a new message. Defaults to /api/support/ticket/[id]/message. */
  endpoint?: string;
  /** Where to POST a single file upload. Defaults to /api/support/attachment. */
  attachmentEndpoint?: string;
  /** Override viewer display name shown beside their reply. */
  viewerName?: string;
  /** After a successful reply, called with the appended message. */
  onReply?: (message: SupportMessage) => void;
  /** IANA timezone for all date renders. Falls back to browser TZ if omitted. */
  tz?: string;
  /** Hide the inline `#N · subject · status` header — useful when the host page already has its own hero. */
  hideHeader?: boolean;
}

const REPLY_ATTACHMENT_MAX = 5;
const REPLY_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

// Our `timestamp without time zone` columns return naive ISO strings
// (no offset). JS parses those as LOCAL, but the stored clock is UTC —
// append `Z` so the parse matches storage.
function parseIso(iso: string): Date {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasOffset ? iso : `${iso}Z`);
}

function formatWhen(iso: string, tz?: string) {
  try {
    return new Intl.DateTimeFormat("en", {
      timeZone: tz,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(parseIso(iso));
  } catch {
    return iso;
  }
}

function AttachmentItem({ attachment }: { attachment: SupportAttachment }) {
  const isImage = attachment.type.startsWith("image/");
  const isVideo = attachment.type.startsWith("video/");

  // No signed URL yet (e.g. optimistic update) — render as a non-clickable
  // chip so the user still sees that the file was attached.
  if (!attachment.url) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-white/60 px-2 py-1 text-[11px] text-gray-700 ring-1 ring-gray-200">
        📎 {attachment.name}
      </span>
    );
  }

  if (isImage) {
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="group block overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:border-primary-300 hover:shadow-sm"
        style={{ maxWidth: 260 }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={attachment.url}
          alt={attachment.name}
          className="max-h-44 w-full object-cover transition group-hover:scale-[1.02]"
        />
        <div className="truncate px-2 py-1 text-[10px] text-gray-600">
          {attachment.name}
        </div>
      </a>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      download={attachment.name}
      className="inline-flex items-center gap-1.5 rounded-md bg-white px-2.5 py-1 text-[11px] text-gray-800 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:ring-primary-300"
    >
      <span aria-hidden>{isVideo ? "🎬" : "📎"}</span>
      <span className="max-w-[160px] truncate">{attachment.name}</span>
      <span aria-hidden className="text-gray-400">↓</span>
    </a>
  );
}

function MessageBubble({
  message,
  isAdmin,
  tz,
}: {
  message: SupportMessage;
  isAdmin: boolean;
  tz?: string;
}) {
  if (message.role === "SYSTEM") {
    return (
      <div className="my-2 text-center text-xs italic text-gray-500">
        {message.body} — {formatWhen(message.createdAt, tz)}
      </div>
    );
  }

  const isInternal = message.visibility === "INTERNAL";
  const isAdminMsg = message.role === "ADMIN";

  if (isInternal && !isAdmin) return null;

  const bubbleClass = isInternal
    ? "bg-yellow-50 border-yellow-300"
    : isAdminMsg
      ? "bg-blue-50 border-blue-200"
      : "bg-gray-50 border-gray-200";

  return (
    <div className={`rounded-lg border px-4 py-3 ${bubbleClass}`}>
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span className="font-medium">
          {message.authorName ??
            message.authorEmail ??
            (isAdminMsg ? "Support" : "You")}
          {isInternal && (
            <span className="ml-2 rounded bg-yellow-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-yellow-900">
              Internal
            </span>
          )}
        </span>
        <span>{formatWhen(message.createdAt, tz)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-900">
        {message.body}
      </p>
      {message.attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {message.attachments.map((a) => (
            <AttachmentItem key={a.path} attachment={a} />
          ))}
        </div>
      )}
    </div>
  );
}

export function SupportTicketThread({
  ticket,
  isAdmin = false,
  endpoint,
  attachmentEndpoint,
  viewerName,
  onReply,
  tz,
  hideHeader = false,
}: SupportTicketThreadProps) {
  const replyEndpoint = endpoint ?? `/api/support/ticket/${ticket.id}/message`;
  const uploadEndpoint = attachmentEndpoint ?? "/api/support/attachment";

  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState<"PUBLIC" | "INTERNAL">("PUBLIC");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localMessages, setLocalMessages] = useState<SupportMessage[]>(
    ticket.messages
  );

  // Reply attachments
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachments, setPendingAttachments] = useState<SupportAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    setAttachmentError(null);
    if (!files || files.length === 0) return;
    if (pendingAttachments.length + files.length > REPLY_ATTACHMENT_MAX) {
      setAttachmentError(`You can attach up to ${REPLY_ATTACHMENT_MAX} files per reply.`);
      return;
    }

    setUploading(true);
    try {
      const next: SupportAttachment[] = [];
      for (const file of Array.from(files)) {
        if (file.size > REPLY_ATTACHMENT_MAX_BYTES) {
          setAttachmentError(`${file.name} is over 25 MB.`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(uploadEndpoint, { method: "POST", body: fd });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setAttachmentError(`Couldn't upload ${file.name}: ${data.error ?? res.statusText}`);
          continue;
        }
        const data = (await res.json()) as SupportAttachment;
        next.push(data);
      }
      setPendingAttachments((prev) => [...prev, ...next]);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function removePending(path: string) {
    setPendingAttachments((prev) => prev.filter((a) => a.path !== path));
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!body.trim() && pendingAttachments.length === 0) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(replyEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          body: body.trim(),
          visibility: isAdmin ? visibility : "PUBLIC",
          authorName: viewerName ?? null,
          attachments: pendingAttachments.map((a) => ({
            path: a.path,
            name: a.name,
            type: a.type,
          })),
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? "Failed to send reply");
      }
      const data = (await res.json()) as { message: SupportMessage };
      setLocalMessages((prev) => [...prev, data.message]);
      setBody("");
      setPendingAttachments([]);
      onReply?.(data.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  const originalAttachments = Array.isArray(ticket.attachments)
    ? ticket.attachments
    : [];

  return (
    <div className="space-y-4">
      {!hideHeader && (
        <header className="border-b pb-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              #{ticket.ticketNumber} · {ticket.subject}
            </h2>
            <span className="rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
              {ticket.status}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Opened {formatWhen(ticket.createdAt, tz)}
          </p>
        </header>
      )}

      <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="text-xs font-medium text-gray-600">Original report</div>
        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
          {ticket.body}
        </p>
        {originalAttachments.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
            {originalAttachments.map((a) => (
              <AttachmentItem key={a.path} attachment={a} />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {localMessages
          .filter((m) => isAdmin || m.visibility === "PUBLIC")
          .map((m) => (
            <MessageBubble key={m.id} message={m} isAdmin={isAdmin} tz={tz} />
          ))}
      </div>

      {ticket.status === "CLOSED" ? (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-700">
          This ticket is closed. Submit a new one if you need more help.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3 border-t pt-4">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
          <Textarea
            label="Your reply"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
          />

          {/* Attached files preview */}
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {pendingAttachments.map((a) => (
                <span
                  key={a.path}
                  className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-800"
                >
                  <span aria-hidden>📎</span>
                  <span className="max-w-[140px] truncate">{a.name}</span>
                  <button
                    type="button"
                    onClick={() => removePending(a.path)}
                    aria-label={`Remove ${a.name}`}
                    className="text-gray-500 hover:text-red-600"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
          {attachmentError && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
              {attachmentError}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 text-sm">
              {isAdmin && (
                <>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={visibility === "PUBLIC"}
                      onChange={() => setVisibility("PUBLIC")}
                    />
                    Public reply
                  </label>
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      checked={visibility === "INTERNAL"}
                      onChange={() => setVisibility("INTERNAL")}
                    />
                    Internal note
                  </label>
                </>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,application/pdf,text/plain,text/csv,application/zip"
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || pendingAttachments.length >= REPLY_ATTACHMENT_MAX}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                title="Attach files"
              >
                <span aria-hidden>📎</span>
                {uploading ? "Uploading…" : "Attach"}
              </button>
            </div>

            <Button type="submit" isLoading={submitting}>
              Send
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
